<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import "maplibre-gl/dist/maplibre-gl.css";
	import maplibregl from "maplibre-gl";
	import type {
		LngLatBoundsLike,
		LayerSpecification,
		SourceSpecification,
	} from "maplibre-gl";
	import bbox from "@turf/bbox";
	import positron from "./extras/positron.svelte";
	import slidesMapStyle from "./extras/slidesMapStyle.svelte";
	const politicalStyle = {
		version: 8,
		name: "Countries",
		sources: {
			openmaptiles: {
				type: "vector",
				url: `https://api.maptiler.com/tiles/v3/tiles.json?key=${import.meta.env.VITE_MAPTILER_KEY}`,
			},
		},
		sprite: "https://openmaptiles.github.io/positron-gl-style/sprite",
		glyphs: `https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=${import.meta.env.VITE_MAPTILER_KEY}`,
		layers: [
			{
				id: "background",
				type: "background",
				paint: { "background-color": "#b3d1ff" },
			},
			{
				id: "water",
				type: "fill",
				source: "openmaptiles",
				"source-layer": "water",
				paint: { "fill-color": "#a3c7df" },
			},
			{
				id: "landcover",
				type: "fill",
				source: "openmaptiles",
				"source-layer": "landcover",
				paint: {
					"fill-color": [
						"match",
						["get", "class"],
						"grass", "#d4e8c5",
						"wood", "#c5dbb8",
						"#e8e4d8"
					],
					"fill-opacity": 0.6,
				},
			},
			{
				id: "boundary-country",
				type: "line",
				source: "openmaptiles",
				"source-layer": "boundary",
				filter: ["==", ["get", "admin_level"], 2],
				paint: {
					"line-color": "#8b4513",
					"line-width": 2,
				},
			},
			{
				id: "place-country",
				type: "symbol",
				source: "openmaptiles",
				"source-layer": "place",
				filter: ["==", ["get", "class"], "country"],
				layout: {
					"text-field": ["get", "name:latin"],
					"text-font": ["Noto Sans Bold"],
					"text-size": 14,
					"text-transform": "uppercase",
				},
				paint: {
					"text-color": "#333",
					"text-halo-color": "#fff",
					"text-halo-width": 2,
				},
			},
		],
	};
	import transformRequest from "./extras/transformRequest.svelte";
	import getTableNames from "./extras/getTableNames.svelte.js";
	import getMapJson from "./extras/getMapJson.svelte";
	import { LayerFadeUtils } from "./extras/fadeOut.svelte";
	import starterLocations from "./extras/starterLocations.json";
	import {
		expandClickedPoint,
		distilled,
		filterEnergized,
		setSourceOpacity,
	} from "./extras/extras.svelte";
	import {
		foundEnergized,
		removeSourceAndLayers,
	} from "./extras/foundEnergized.svelte";
	import {
		showEnergyBalance,
		clearEnergyBalance,
	} from "./extras/energyBalance.svelte";
	import { layersOfImportance } from "./extras/layersOfImport.svelte";
	import { gpsPulsingDot, highlightedItem } from "./extras/extraIcons.svelte";
	import outageDivSupply from "./extras/outageIcon.svelte";
	import {
		initOutageMarkers,
		updateOutageMarkers,
		clearOutageMarkers,
	} from "./extras/outageMarkers";
	import {
		showChronicleDiff,
		refreshChronicleDiff,
		clearChronicleDiff,
		LAYER_IDS as CHRONICLE_DIFF_LAYERS,
	} from "./extras/chronicleDiff.svelte";
	import { dldl, goto, yyyy, zzzz } from "../store.svelte";
	import { toast } from "../lib/toast.svelte.ts";
	import { countries, countryNames } from "../extras/countries";

	const uri: string = yyyy.uri.api;

	let hiaid: number; //Highlited Item animation ID
	let map = null; // Store map reference at module level for cleanup

	let captchaActive: boolean = $state(false);
	onMount(() => {
		if (yyyy.map) delete yyyy.map.idnt;
		if (yyyy.ctrl) {
			delete yyyy.ctrl.expanded;
			delete yyyy.ctrl.control;
		}

		let outages = {};

		// Pick a random starter location with error handling
		let mapCenter = [0, 0]; // Default to Null Island
		let mapZoom = 4; // Default zoom
		let locationName = "Null Island (default)";

		try {
			if (starterLocations?.locations && Array.isArray(starterLocations.locations) && starterLocations.locations.length > 0) {
				const randomLocation = starterLocations.locations[
					Math.floor(Math.random() * starterLocations.locations.length)
				];

				// Validate the selected location has required properties
				if (randomLocation?.center && Array.isArray(randomLocation.center) &&
					randomLocation.center.length === 2 &&
					typeof randomLocation.center[0] === 'number' &&
					typeof randomLocation.center[1] === 'number' &&
					typeof randomLocation.zoom === 'number') {

					mapCenter = randomLocation.center;
					mapZoom = randomLocation.zoom;
					locationName = randomLocation.name || "Unknown Location";
				} else {
					console.warn("Invalid location data, defaulting to Null Island:", randomLocation);
				}
			} else {
				console.warn("No valid locations found in starterLocations.json, defaulting to Null Island");
			}
		} catch (error) {
			console.error("Error loading starter location, defaulting to Null Island:", error);
		}

		map = new maplibregl.Map({
			container: "maplibre-map",
			maxZoom: 25,
			style: positron,
			center: mapCenter,
			attributionControl: false,
			dragRotate: false,
			zoom: mapZoom,
			touchZoomRotate: true,
			transformRequest: transformRequest,
		});

		// Log the selected location
		console.log(`Map initialized at: ${locationName}`);

		map.on("load", () => {
			// Show location toast in bottom right after a short delay to ensure everything is rendered
			setTimeout(() => {
				toast.ghost(` ${locationName}`, 8775, "bottom-right", 'horizontal', false, false, true);
			}, 500);
		});

		map.on("moveend", () => {
			yyyy.map.location = { coords: map.getCenter(), z: map.getZoom() };
			if (map.getZoom() > 16 && !yyyy.map.tierSelector) {
				yyyy.map.tierSelector = true;
				// When zooming in, keep existing filter states
				yyyy.map.showSecondary = true;
				zzzz.emit("handleTierFilter");
			} else if (map.getZoom() <= 16 && yyyy.map.tierSelector) {
				yyyy.map.tierSelector = false;
				// When zooming out, reset to show all
				yyyy.map.showPrimary = true;
				yyyy.map.showSecondary = true;
				yyyy.map.showAdjunct = false;
				zzzz.emit("handleTierFilter");
				setTimeout(() => {
					zzzz.emit("handleAdjunct");
				}, 100);
			}
			refreshChronicleDiff(map);
		});

		map.on("click", (e) => {
			// When Spotlight is active, don't process identify clicks
			if (yyyy.ctrl.control === "Spotlight") return;

			// When Chronicle is active, query diff layers instead
			if (yyyy.ctrl.control === "Chronicle") {
				let queryBox = expandClickedPoint(map, 5, e.lngLat);
				let diffLayers = CHRONICLE_DIFF_LAYERS.filter((id) => map.getLayer(id));
				if (diffLayers.length > 0) {
					let hits = map.queryRenderedFeatures(queryBox, { layers: diffLayers });
					if (hits.length > 0) {
						console.log("Chronicle diff click:", hits.map((f) => f.properties));
					}
				}
				return;
			}

			delete yyyy.map.idnt;
			zzzz.emit("clear-energized");
			zzzz.emit("map-click");
			map.panTo(e.lngLat);
			let queryBox = expandClickedPoint(map, 5, e.lngLat);

			let qrf = map.queryRenderedFeatures(queryBox, {
				layers: layersOfImportance,
			});

			console.log("qrf is: ", qrf);
			let idnt = distilled(qrf, yyyy.sesh.activeLivewire.wireident, e.lngLat);
			console.log("idnt is: ", idnt);

			// If we have outages calculator open, let it handle click events
			if (yyyy.ctrl.control === "OutageCalculator" /*&& idnt.length > 0*/) {
				// We're filtering out electric nodes. in the f
				setTimeout(() => {
					yyyy.map.idnt = idnt.filter(
						(item) =>
							item.sourceLayer === "nodes" && item.properties?.status !== "ADJUNCT",
					);
					// Emit the mapClick event with the features
					//zzzz.emit("mapClick", { features: idnt, lngLat: e.lngLat });
				}, 150);
				return;
			}

			if (idnt.length === 0) {
				console.log("nothing ");
				delete yyyy.ctrl.expanded;
				return;
			}
			yyyy.ctrl.interaction = "mapClick";

			setTimeout(() => {
				yyyy.map.idnt = idnt;
			}, 150);

			// Only reset expanded and control if we're not already in OutageCalculator
			if (yyyy.ctrl.control !== "OutageCalculator") {
				delete yyyy.ctrl.expanded;
				delete yyyy.ctrl.control;
				yyyy.ctrl.expanded = true;
				yyyy.ctrl.control = "Idented";
			}
		});

		zzzz.on("zoom", (e) => {
			if (e === "+") map.zoomIn();
			if (e === "-") map.zoomOut();
		});

		zzzz.on("captcha", (countryName) => {
			// Find the country object with bounding box info
			const country = countries.find((c) => c.name === countryName);
			captchaActive = true;

			// Function to add bbox and pan to country
			const showCountry = () => {
				// Remove existing captcha bounding box if it exists
				if (map.getSource("captcha-bbox")) {
					map.removeLayer("captcha-bbox-line");
					map.removeSource("captcha-bbox");
				}

				if (country && country.bbox) {
					// bbox format is [west, south, east, north]
					const [west, south, east, north] = country.bbox;

					// Convert to MapLibre bounds format [[west, south], [east, north]]
					const bounds = [
						[west, south],
						[east, north],
					];

					// Create GeoJSON rectangle for the bounding box
					const bboxGeoJSON = {
						type: "Feature",
						geometry: {
							type: "Polygon",
							coordinates: [
								[
									[west, south],
									[east, south],
									[east, north],
									[west, north],
									[west, south],
								],
							],
						},
					};

					// Add the bounding box source and layer
					map.addSource("captcha-bbox", {
						type: "geojson",
						data: bboxGeoJSON,
					});

					map.addLayer({
						id: "captcha-bbox-line",
						type: "line",
						source: "captcha-bbox",
						paint: {
							"line-color": "#ff3e00",
							"line-width": 3,
							"line-dasharray": [5, 5],
							"line-opacity": 0.8,
						},
					});

					// Fit the map to the country's bounding box
					map.fitBounds(bounds, {
						padding: { bottom: 200 },
						duration: 2000,
						maxZoom: 6, // Prevent zooming too close for large countries
					});

					console.log(`Panning to ${countryName}`, bounds);
				} else {
					console.warn(`Country not found or missing bbox: ${countryName}`);
				}
			};

			// Switch to political style if not already active
			const currentStyle = map.getStyle();
			if (!currentStyle?.name?.includes("Countries")) {
				map.setStyle(politicalStyle);
				map.once("idle", showCountry);
			} else {
				// Already on political style, just show the country
				showCountry();
			}
		});

		zzzz.on("captcha-end", () => {
			captchaActive = false;
			// Switch back to positron style (diff: false to avoid sprite mismatch error)
			map.setStyle(positron, { diff: false });
			console.log("Captcha ended, switching back to positron");
		});

		// --- Spotlight: Polygon Drawing ---
		let drawPoints: [number, number][] = [];
		let isDrawing = false;
		let drawClickHandler: ((e: any) => void) | null = null;

		const updateDrawPolygon = () => {
			const coords = [...drawPoints];
			// Update vertex markers
			const vertexGeoJSON = {
				type: "FeatureCollection",
				features: drawPoints.map(pt => ({
					type: "Feature",
					geometry: { type: "Point", coordinates: pt },
					properties: {},
				})),
			};
			if (map.getSource("context-draw-vertices")) {
				map.getSource("context-draw-vertices").setData(vertexGeoJSON);
			}

			// Update polygon/line preview
			let previewGeoJSON: any;
			if (coords.length >= 3) {
				previewGeoJSON = {
					type: "Feature",
					geometry: {
						type: "Polygon",
						coordinates: [[...coords, coords[0]]],
					},
					properties: {},
				};
			} else if (coords.length === 2) {
				previewGeoJSON = {
					type: "Feature",
					geometry: { type: "LineString", coordinates: coords },
					properties: {},
				};
			} else {
				previewGeoJSON = {
					type: "FeatureCollection",
					features: [],
				};
			}
			if (map.getSource("context-draw-polygon")) {
				map.getSource("context-draw-polygon").setData(previewGeoJSON);
			}
		};

		const cleanupDrawLayers = () => {
			["context-draw-fill", "context-draw-outline", "context-draw-vertices-layer"].forEach(id => {
				if (map.getLayer(id)) map.removeLayer(id);
			});
			["context-draw-polygon", "context-draw-vertices"].forEach(id => {
				if (map.getSource(id)) map.removeSource(id);
			});
		};

		const stopDrawing = () => {
			if (drawClickHandler) {
				map.off("click", drawClickHandler);
				drawClickHandler = null;
			}
			map.getCanvas().style.cursor = "";
			isDrawing = false;
			drawPoints = [];
		};

		zzzz.on("startContextDraw", () => {
			// Clean up any previous drawing
			stopDrawing();
			cleanupDrawLayers();
			drawPoints = [];
			isDrawing = true;

			// Pad the map so the panel doesn't obscure the view
			const panelWidth = Math.max(window.innerWidth * 0.35, 450) + 30;
			map.easeTo({ padding: { left: panelWidth, top: 15, right: 15, bottom: 15 }, duration: 300 });

			map.getCanvas().style.cursor = "crosshair";

			// Add sources
			map.addSource("context-draw-polygon", {
				type: "geojson",
				data: { type: "FeatureCollection", features: [] },
			});
			map.addSource("context-draw-vertices", {
				type: "geojson",
				data: { type: "FeatureCollection", features: [] },
			});

			// Add layers
			map.addLayer({
				id: "context-draw-fill",
				type: "fill",
				source: "context-draw-polygon",
				paint: {
					"fill-color": "#ff3e00",
					"fill-opacity": 0.1,
				},
			});
			map.addLayer({
				id: "context-draw-outline",
				type: "line",
				source: "context-draw-polygon",
				paint: {
					"line-color": "#ff3e00",
					"line-width": 2,
					"line-dasharray": [4, 4],
				},
			});
			map.addLayer({
				id: "context-draw-vertices-layer",
				type: "circle",
				source: "context-draw-vertices",
				paint: {
					"circle-radius": 5,
					"circle-color": "#ff3e00",
					"circle-stroke-color": "#fff",
					"circle-stroke-width": 2,
				},
			});

			drawClickHandler = (e: any) => {
				if (!isDrawing) return;
				drawPoints.push([e.lngLat.lng, e.lngLat.lat]);
				updateDrawPolygon();
				zzzz.emit("contextPolygonPoint");
			};

			map.on("click", drawClickHandler);
		});

		zzzz.on("finishContextDraw", () => {
			if (!isDrawing || drawPoints.length < 3) return;

			const polygon = {
				type: "Polygon",
				coordinates: [[...drawPoints, drawPoints[0]]],
			};

			// Keep the polygon visible but stop drawing
			isDrawing = false;
			if (drawClickHandler) {
				map.off("click", drawClickHandler);
				drawClickHandler = null;
			}
			map.getCanvas().style.cursor = "";

			// Zoom to the polygon
			const bounds = bbox(polygon);
			const padding = { left: 50, top: 50, right: 50, bottom: 50 };
			map.fitBounds(bounds as LngLatBoundsLike, {
				padding,
				duration: 500,
			});

			zzzz.emit("contextPolygonComplete", polygon);
		});

		zzzz.on("endContextDraw", () => {
			stopDrawing();
			cleanupDrawLayers();
			// Reset map padding
			map.easeTo({ padding: { left: 0, top: 0, right: 0, bottom: 0 }, duration: 300 });
		});

		zzzz.on("clearContextPolygon", () => {
			cleanupDrawLayers();
			drawPoints = [];
		});
		// --- End Spotlight ---

		zzzz.on("map-active", async () => {
			dldl(
				"Maplibre.svelte",
				`map is active with ${yyyy.sesh.activeLivewire.livewire}`,
			);
			map.fitBounds(bbox(yyyy.sesh.activeLivewire.extent) as LngLatBoundsLike, {
				animate: false,
				padding: { top: 50, left: 50, right: 50, bottom: 50 },
			});

			yyyy.map.tableNames = await getTableNames();
			(yyyy.map.showPrimary = true),
				(yyyy.map.showSecondary = true),
				(yyyy.map.showAdjunct = false);
			map.setMaxBounds(map.getBounds());
			map.setStyle(await getMapJson());
			let layers = map.getStyle().layers;

			layers.forEach((layer: LayerSpecification) => {
				if (
					"source" in layer &&
					layer.source === yyyy.sesh.activeLivewire.wireident
				) {
					map.on("mouseenter", layer.id, () => {
						map.getCanvas().style.cursor = "pointer";
					});
					map.on("mouseleave", layer.id, () => {
						map.getCanvas().style.cursor = "";
					});
				}
			});
		});

		zzzz.on("handlePhase", (phase: string) => {
			dldl("Maplibre.svelte", `Toggling phase ${phase}`);
			yyyy.map.phases[phase] = !yyyy.map.phases[phase];
			if (yyyy.map.phases[phase]) {
				map.setLayoutProperty(`LVWR:LINE-PHASE-${phase}`, "visibility", "visible");
			} else {
				map.setLayoutProperty(`LVWR:LINE-PHASE-${phase}`, "visibility", "none");
			}
		});

		zzzz.on("handleSatellite", () => {
			let y = map.getStyle().sources;
			let layers = map.getStyle().layers;

			if (map.getLayoutProperty("satellite", "visibility") == "none") {
				layers.forEach((layer) => {
					if (layer.source === "openmaptiles") {
						map.setLayoutProperty(layer.id, "visibility", "none");
					}
					map.setLayoutProperty("satellite", "visibility", "visible");
				});
				yyyy.map.isSatellite = true;
			} else {
				layers.forEach((layer: LayerSpecification) => {
					if (layer.source === "openmaptiles") {
						map.setLayoutProperty(layer.id, "visibility", "visible");
					}
					map.setLayoutProperty("satellite", "visibility", "none");
				});
				yyyy.map.isSatellite = false;
			}
		});

		// Handle tier filtering for Primary/Secondary
		zzzz.on("handleTierFilter", () => {
			dldl("Maplibre.svelte", "Updating tier filters");

			// Build allowed tiers array - always include 0 (transformers)
			let allowedTiers = [0];
			if (yyyy.map.showPrimary) allowedTiers.push(1);
			if (yyyy.map.showSecondary) allowedTiers.push(2);

			// Get visible table names for combining with tier filter
			const visibleTables = yyyy.map.tableNames
				.filter((obj) => obj.vis)
				.map((obj) => obj.tablename);

			// Update line layers with tier filter
			map.setFilter("LVWR:LINE-DEENERGIZED", [
				"all",
				["!has", "feederid"],
				["in", "lw_table", ...visibleTables],

				//["in", "tier", ...allowedTiers]
			]);

			map.setFilter("LVWR:LINE-PHASE-A", [
				"all",
				["in", "A", ["get", "phase"]],
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]],

				["in", ["get", "tier"], ["literal", allowedTiers]],
				//["in", "tier", ...allowedTiers]
			]);

			map.setFilter("LVWR:LINE-PHASE-B", [
				"all",
				["in", "B", ["get", "phase"]],
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]],
				//["in", "tier", ...allowedTiers]
				["in", ["get", "tier"], ["literal", allowedTiers]],
			]);

			map.setFilter("LVWR:LINE-PHASE-C", [
				"all",
				["in", "C", ["get", "phase"]],
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]],
				["in", ["get", "tier"], ["literal", allowedTiers]],
			]);

			// Update device layers with tier filter
			map.setFilter("LVWR:DEVICES-ABC", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "ABC"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-AB", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "AB"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-AC", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "AC"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-BC", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "BC"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-A", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "A"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-B", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "B"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-C", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "C"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-SOURCE", [
				"all",
				["==", "status", "SOURCE"],
				["in", "lw_table", ...visibleTables],
				["in", "tier", ...allowedTiers],
			]);
			// Update other layers that might have tier field
			map.setFilter("LVWR:LOAD", [
				"all",
				["==", "status", "LOAD"],
				["==", "energized", true],
				["in", "lw_table", ...visibleTables],
				//["in", ["get", "tier"], ["literal", allowedTiers]]
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-DENERGIZED", [
				"all",
				["==", "energized", false],
				["in", "lw_table", ...visibleTables],
				//["in", ["get", "tier"], ["literal", allowedTiers]]
				["in", "tier", ...allowedTiers],
			]);

			map.setFilter("LVWR:DEVICES-UNENERGIZED", [
				"all",
				["!has", "feederid"],
				["==", "energized", true],
				["in", "lw_table", ...visibleTables],
				["!=", "status", "ADJUNCT"],
				["in", ["get", "tier"], ["literal", allowedTiers]],
			]);

			map.setFilter("LVWR:Flow-Direction", [
				"all",
				["has", "feederid"],
				["!has", "issues"],
				["in", "lw_table", ...visibleTables],
				//["in", ["get", "tier"], ["literal", allowedTiers]]
				["in", "tier", ...allowedTiers],
			]);


			map.setFilter("LVWR:Feeder", [
				"all",
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]],

				["in", ["get", "tier"], ["literal", allowedTiers]],
				//["in", "tier", ...allowedTiers],
			]);


		});

		// Handle adjunct layer visibility toggle
		zzzz.on("handleAdjunct", () => {
			dldl("Maplibre.svelte", `Toggling adjunct layer: ${yyyy.map.showAdjunct}`);
			const visibility = yyyy.map.showAdjunct ? "none" : "visible";
			map.setLayoutProperty("LVWR:ADJUNCT", "visibility", visibility);
		});

		zzzz.on("handleLayer", (layer: string) => {
			dldl("Maplibre.svelte", `Toggling the following layer: ${layer}`);
			// Get layers from the map that match the active livewire
			let layers = map
				.getStyle()
				.layers.filter((_) => _.source === yyyy.sesh.activeLivewire.wireident);
			//console.log(layers);
			// Toggle visibility for the layer that matches the provided layer name
			yyyy.map.tableNames.forEach(
				(l) => l.tablename === layer && (l.vis = !l.vis),
			);
			// Get an array of visible table names
			const visibleTables = yyyy.map.tableNames
				.filter((obj) => obj.vis)
				.map((obj) => obj.tablename);
			//console.log("visibleTables: ", visibleTables);

			map.setFilter("LVWR:LINE-DEENERGIZED", [
				"all",
				["!has", "feederid"],
				["in", "lw_table", ...visibleTables],
			]);

			map.setFilter("LVWR:LINE-PHASE-A", [
				"all",
				["in", "A", ["get", "phase"]],
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]], // Use spread operator if visibleTables is an array
			]);

			map.setFilter("LVWR:LINE-PHASE-B", [
				"all",
				["in", "B", ["get", "phase"]],
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]], // Use spread operator if visibleTables is an array
			]);

			map.setFilter("LVWR:LINE-PHASE-C", [
				"all",
				["in", "C", ["get", "phase"]],
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]], // Use spread operator if visibleTables is an array
			]);

			map.setFilter("LVWR:LOAD", [
				"all",
				["==", "status", "LOAD"],
				["==", "energized", true],
				["in", "lw_table", ...visibleTables],
			]);

			map.setFilter("LVWR:DEVICES-ABC", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "ABC"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-AB", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "AB"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-AC", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "AC"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-BC", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "BC"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-A", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "A"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-B", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "B"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-C", [
				"all",
				["==", "status", "DEVICE"],
				["==", "energized", true],
				["==", "phase", "C"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Transformers-ABC", [
				"all",
				["==", "status", "TRANSFORMER"],
				["==", "energized", true],
				["==", "phase", "ABC"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Transformers-AB", [
				"all",
				["==", "status", "TRANSFORMER"],
				["==", "energized", true],
				["==", "phase", "AB"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Transformers-BC", [
				"all",
				["==", "status", "TRANSFORMER"],
				["==", "energized", true],
				["==", "phase", "BC"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Transformers-AC", [
				"all",
				["==", "status", "TRANSFORMER"],
				["==", "energized", true],
				["==", "phase", "AC"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Transformers-A", [
				"all",
				["==", "status", "TRANSFORMER"],
				["==", "energized", true],
				["==", "phase", "A"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Transformers-B", [
				"all",
				["==", "status", "TRANSFORMER"],
				["==", "energized", true],
				["==", "phase", "B"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Transformers-C", [
				"all",
				["==", "status", "TRANSFORMER"],
				["==", "energized", true],
				["==", "phase", "C"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-DENERGIZED", [
				"all",
				["==", "energized", false],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:DEVICES-UNENERGIZED", [
				"all",
				["!has", "feederid"],
				["==", "energized", true],
				["in", "lw_table", ...visibleTables],
				["!=", "status", "ADJUNCT"],
			]);
			map.setFilter("LVWR:DEVICES-SOURCE", [
				"all",
				["==", "status", "SOURCE"],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:LOAD", [
				"all",
				["==", "status", "LOAD"],
				["==", "energized", true],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:ADJUNCT", [
				"all",
				["==", "status", "ADJUNCT"],
				["==", "energized", true],
				["in", "lw_table", ...visibleTables],
			]);
			map.setFilter("LVWR:Flow-Direction", [
				"all",
				["has", "feederid"],
				["!has", "issues"],
				["in", "lw_table", ...visibleTables],
			]);
			// map.setFilter("LVWR:Ambiguous-Flow", [
			// 	"all",
			// 	["has", "feederid"],
			// 	["==", "issues", '{"Electric Loop": true}'],
			// 	["in", ["get", "lw_table"], ["literal", visibleTables]],
			// ]);
			// map.setFilter("LVWR:Node-Phase-Mismatch", [
			// 	"all",
			// 	["has", "feederid"],
			// 	["has", "issues"],
			// 	["in", ["get", "lw_table"], ["literal", visibleTables]],
			// ]);
			map.setFilter("LVWR:Feeder", [
				"all",
				["has", "feederid"],
				["in", ["get", "lw_table"], ["literal", visibleTables]],
			]);
		});

		/******************************/

		let hiaid: number;
		let currentLayerType: "line" | "circle" | null = null;

		async function getFeatureBounds(sourceId, sourceLayer, featureId) {
			const features = map.querySourceFeatures(sourceId, {
				sourceLayer: sourceLayer,
				filter: ["==", ["get", "lw_table_pkid"], featureId],
			});

			if (features.length > 0) {
				return features[0].geometry;
			}
			return null;
		}

		// Initialize the highlight layers once
		function initHighlightLayers(ss) {
			if (!map.getLayer("highlighted-feature-line")) {
				map.addLayer({
					id: "highlighted-feature-line",
					type: "line",
					source: ss.id,
					"source-layer": "lines",
					layout: {
						visibility: "none",
					},
					paint: {
						"line-color": "#ff3e00",
						"line-width": 4,
						"line-opacity": 1,
					},
					filter: ["==", ["get", "lw_table_pkid"], ""],
				});
			}

			if (!map.getLayer("highlighted-feature-circle")) {
				map.addLayer({
					id: "highlighted-feature-circle",
					type: "circle",
					source: ss.id,
					"source-layer": "nodes",
					layout: {
						visibility: "none",
					},
					paint: {
						"circle-radius": 6,
						"circle-color": "#ff3e00",
						"circle-opacity": 1,
					},
					filter: ["==", ["get", "lw_table_pkid"], ""],
				});
			}
		}

		zzzz.on("goToFeeder", (what) => {
			console.log("@goToFeder: ", what);
			map.fitBounds(bbox(what), {
				padding: { top: 15, bottom: 15, left: 450, right: 15 },
				animate: false,
			});

			map.addSource("flash-bang", { type: "geojson", data: what });

			map.addLayer({
				id: "flash-bang",
				type: "line",
				source: "flash-bang",

				layout: {
					"line-join": "round",
					"line-cap": "round",
				},
				paint: {
					"line-color": "#ff3e00",
					"line-width": 5,
					"line-dasharray": [25, 5],
					//"line-gap-width": 15,
				},
			});
			LayerFadeUtils.fadeOut(map, "flash-bang", 7000).then(() => {
				map.removeLayer("flash-bang");
				map.removeSource("flash-bang");
			});
		});

		zzzz.on("showTieDevicesOnMap", (fc) => {
			console.log("@showTieDevicesOnMap: ", fc);

			if (map.getLayer("tie-devices")) map.removeLayer("tie-devices");
			if (map.getSource("tie-devices")) map.removeSource("tie-devices");

			if (fc.features && fc.features.length > 0) {
				map.addSource("tie-devices", { type: "geojson", data: fc });
				map.addLayer({
					id: "tie-devices",
					type: "circle",
					source: "tie-devices",
					paint: {
						"circle-radius": 8,
						"circle-color": "#e67e22",
						"circle-stroke-color": "#ffffff",
						"circle-stroke-width": 2,
						"circle-opacity": 0.9,
					},
				});
				map.addLayer({
					id: "tie-devices-pulse",
					type: "circle",
					source: "tie-devices",
					paint: {
						"circle-radius": 14,
						"circle-color": "#e67e22",
						"circle-opacity": 0.4,
					},
					filter: ["==", ["get", "lw_table_pkid"], ""],
				});
				map.fitBounds(bbox(fc), {
					padding: { top: 50, bottom: 50, left: 450, right: 50 },
					animate: true,
				});
			}
		});

		zzzz.on("goToTieDevice", (geom) => {
			console.log("@goToTieDevice: ", geom);
			const coords = geom.coordinates;
			map.flyTo({ center: [coords[0], coords[1]], zoom: 18 });

			const pointFc = {
				type: "FeatureCollection",
				features: [{
					type: "Feature",
					geometry: geom,
					properties: {},
				}],
			};

			if (map.getSource("tie-flash")) {
				map.removeLayer("tie-flash");
				map.removeSource("tie-flash");
			}
			map.addSource("tie-flash", { type: "geojson", data: pointFc });
			map.addLayer({
				id: "tie-flash",
				type: "circle",
				source: "tie-flash",
				paint: {
					"circle-radius": 14,
					"circle-color": "#ff3e00",
					"circle-opacity": 0.8,
				},
			});
			LayerFadeUtils.fadeOut(map, "tie-flash", 5000).then(() => {
				if (map.getLayer("tie-flash")) map.removeLayer("tie-flash");
				if (map.getSource("tie-flash")) map.removeSource("tie-flash");
			});
		});

		zzzz.on("highlightTieDevice", (pkid) => {
			if (map.getLayer("tie-devices-pulse")) {
				map.setFilter("tie-devices-pulse", ["==", ["get", "lw_table_pkid"], pkid]);
			}
			if (map.getLayer("tie-devices")) {
				map.setPaintProperty("tie-devices", "circle-radius", [
					"case",
					["==", ["get", "lw_table_pkid"], pkid], 12,
					8,
				]);
			}
		});

		zzzz.on("unhighlightTieDevice", () => {
			if (map.getLayer("tie-devices-pulse")) {
				map.setFilter("tie-devices-pulse", ["==", ["get", "lw_table_pkid"], ""]);
			}
			if (map.getLayer("tie-devices")) {
				map.setPaintProperty("tie-devices", "circle-radius", 8);
			}
		});

		zzzz.on("hideTieDevices", () => {
			if (map.getLayer("tie-devices-pulse")) map.removeLayer("tie-devices-pulse");
			if (map.getLayer("tie-devices")) map.removeLayer("tie-devices");
			if (map.getSource("tie-devices")) map.removeSource("tie-devices");
		});

		zzzz.on("goToAnnotation", (gpsData) => {
			console.log("@goToAnnotation event received!");
			console.log("GPS Data:", gpsData);
			console.log("GPS Data type:", typeof gpsData);
			console.log("GPS Data structure:", JSON.stringify(gpsData, null, 2));

			// Extract coordinates from GeoJSON point
			let coordinates;
			if (gpsData.type === "Point" && gpsData.coordinates) {
				console.log("Detected GeoJSON Point format");
				coordinates = gpsData.coordinates;
			} else if (Array.isArray(gpsData) && gpsData.length === 2) {
				console.log("Detected array format");
				coordinates = gpsData;
			} else {
				console.error("Invalid GPS data format:", gpsData);
				console.error("Expected GeoJSON Point or [lng, lat] array");
				return;
			}

			console.log("Extracted coordinates:", coordinates);

			// Pan to the annotation location
			map.flyTo({
				center: coordinates,
				zoom: 18,
				padding: { top: 15, bottom: 15, left: 450, right: 15 },
				essential: true
			});

			// Create a GeoJSON point for the marker
			const pointData = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: coordinates
				},
				properties: {}
			};

			// Remove existing annotation marker if present
			if (map.getSource("annotation-marker")) {
				if (map.getLayer("annotation-marker")) {
					map.removeLayer("annotation-marker");
				}
				map.removeSource("annotation-marker");
			}

			// Add the annotation marker
			map.addSource("annotation-marker", {
				type: "geojson",
				data: pointData
			});

			map.addLayer({
				id: "annotation-marker",
				type: "circle",
				source: "annotation-marker",
				paint: {
					"circle-radius": 12,
					"circle-color": "#ff3e00",
					"circle-opacity": 0.8,
					"circle-stroke-width": 3,
					"circle-stroke-color": "#ffffff",
					"circle-stroke-opacity": 1
				},
			});

			// Fade out and remove the marker after 5 seconds
			LayerFadeUtils.fadeOut(map, "annotation-marker", 5000).then(() => {
				if (map.getLayer("annotation-marker")) {
					map.removeLayer("annotation-marker");
				}
				if (map.getSource("annotation-marker")) {
					map.removeSource("annotation-marker");
				}
			});
		});

		zzzz.on("makeLarge", async (e) => {
			// console.log("-----------------");
			// console.log($state.snapshot(e));
			// console.log("-----------------");
			// console.log("-----------------");
			// Cancel any existing animation
			if (hiaid) {
				cancelAnimationFrame(hiaid);
			}

			let source = map.getSource(e.source);

			// Initialize layers if needed
			initHighlightLayers(source);

			// Hide both layers first
			map.setLayoutProperty("highlighted-feature-line", "visibility", "none");
			map.setLayoutProperty("highlighted-feature-circle", "visibility", "none");

			if (e.layer.type === "line") {
				map.setFilter("highlighted-feature-line", [
					"==",
					["get", "lw_table_pkid"],
					e.properties.lw_table_pkid,
				]);
				map.setLayoutProperty("highlighted-feature-line", "visibility", "visible");
				currentLayerType = "line";

				function animateLine(timestamp) {
					const progress = (timestamp % 1500) / 1500;
					map.setPaintProperty("highlighted-feature-line", "line-width", [
						"interpolate",
						["linear"],
						["number", progress],
						0,
						4,
						0.5,
						8,
						1,
						4,
					]);
					map.setPaintProperty("highlighted-feature-line", "line-opacity", [
						"interpolate",
						["linear"],
						["number", progress],
						0,
						0.8,
						0.5,
						1,
						1,
						0.8,
					]);
					hiaid = requestAnimationFrame(animateLine);
				}
				animateLine(0);
			}

			if (e.layer.type === "symbol") {
				map.setFilter("highlighted-feature-circle", [
					"==",
					["get", "lw_table_pkid"],
					e.properties.lw_table_pkid,
				]);
				map.setLayoutProperty(
					"highlighted-feature-circle",
					"visibility",
					"visible",
				);
				currentLayerType = "circle";

				function animatePulse(timestamp) {
					map.setPaintProperty("highlighted-feature-circle", "circle-radius", [
						"interpolate",
						["linear"],
						["number", timestamp % 1000],
						0,
						6,
						1000,
						20,
					]);
					map.setPaintProperty("highlighted-feature-circle", "circle-opacity", [
						"interpolate",
						["linear"],
						["number", timestamp % 1000],
						0,
						1,
						1000,
						0,
					]);
					hiaid = requestAnimationFrame(animatePulse);
				}
				animatePulse(0);
			}

			const geometry =
				e.geometry ||
				e.properties.geom ||
				(await getFeatureBounds(
					source.id,
					e.layer.type === "line" ? "lines" : "nodes",
					e.properties.lw_table_pkid,
				));

			// console.log("::::", $state.snapshot(geometry));
			// console.log("Current Zoom: ", map.getZoom());

			if (!geometry || geometry.type !== "Point") return;
			if (map.getZoom() < 15 && geometry.type === "Point") {
				console.log("use fly to");
				map.flyTo({
					center: [geometry.coordinates[0], geometry.coordinates[1]],
					zoom: 17,
				});
			} else {
				map.panTo(geometry.coordinates, { duration: 1877.5 });
			}

			/*		This if block is to pan or fit around linestrings  */

			// if (geometry) {
			// 	// Get the bounds of the geometry
			// 	let bounds;
			// 	if (geometry.type === "Point") {
			// 		const [lng, lat] = geometry.coordinates;
			// 		// Create a small bounding box around the point
			// 		bounds = [
			// 			[lng - 0.001, lat - 0.001],
			// 			[lng + 0.001, lat + 0.001],
			// 		];
			// 	} else if (
			// 		geometry.type === "LineString" ||
			// 		geometry.type === "MultiLineString"
			// 	) {
			// 		const coordinates =
			// 			geometry.type === "LineString"
			// 				? geometry.coordinates
			// 				: geometry.coordinates.flat();
			//
			// 		bounds = coordinates.reduce(
			// 			([min, max], coord) => {
			// 				return [
			// 					[Math.min(min[0], coord[0]), Math.min(min[1], coord[1])],
			// 					[Math.max(max[0], coord[0]), Math.max(max[1], coord[1])],
			// 				];
			// 			},
			// 			[
			// 				[180, 90],
			// 				[-180, -90],
			// 			],
			// 		);
			// 	}
			//
			// 	if (bounds) {
			// 		console.log(e)
			// 		let currentZoom = map.getZoom()
			// 		map.fitBounds(bounds, {
			// 			padding: 50,
			// 			duration: 1000,
			// 			minZoom: currentZoom,
			// 		});
			// 	}
			// }
		});

		zzzz.on("makeSmall", () => {
			if (hiaid) {
				cancelAnimationFrame(hiaid);
			}

			// Hide both layers
			map.setLayoutProperty("highlighted-feature-line", "visibility", "none");
			map.setLayoutProperty("highlighted-feature-circle", "visibility", "none");
			currentLayerType = null;
		});

		zzzz.on("foundEnergized", (e) => {
			console.log("----------------");
			console.log(e);
			console.log("----------------");

			zzzz.emit("clear-energized");

			setSourceOpacity(map, yyyy.sesh.activeLivewire.wireident, 0.2);

			yyyy.ctrl.interaction = "mapEnergized";
			foundEnergized(map, e);

			console.log(bbox(e.findenergized));

			let d1 = filterEnergized(e, "nrgzd");
			console.log(d1);
			let d2 = distilled(d1, "nrgzd");
			console.log(d2);
			yyyy.map.idnt = d2;

			map.fitBounds(bbox(e.findenergized), {
				padding: { top: 25, bottom: 25, left: 275, right: 75 },
			});
			// menuActive = false;
		});

		zzzz.on("clear-energized", () => {
			//clearInterval(terval);
			//clickshow.remove();

			removeSourceAndLayers(map, "nrgzd");
			setSourceOpacity(map, yyyy.sesh.activeLivewire.wireident, 1);
		});

		zzzz.on("showEnergyBalance", (geojson) => {
			showEnergyBalance(map, geojson);
			if (geojson.features && geojson.features.length > 0) {
				map.fitBounds(bbox(geojson), {
					padding: { top: 50, bottom: 50, left: 300, right: 50 },
				});
			}
		});

		zzzz.on("clearEnergyBalance", () => {
			clearEnergyBalance(map);
		});

		zzzz.on("chronicle-diff-show", ({ oldWireident, newWireident }) => {
			showChronicleDiff(map, oldWireident, newWireident);
		});

		zzzz.on("chronicle-diff-hide", () => {
			clearChronicleDiff(map);
		});

		zzzz.on("map-dormant", () => {
			goto("/livewires");

			map.setStyle(positron);
			delete yyyy.sesh.activeLivewire;
			delete yyyy.map.ident;
			delete yyyy.map.search;
			delete yyyy.map.foot;
			map.setMaxBounds();
			map.zoomTo(4, { duration: 2000, offset: [100, 50] });
			map.fitBounds([
				[-79, -80],
				[80, 80],
			]);
		});

		// GPS Location tracking
		let gpsMarker = null;

		zzzz.on("pos.info", (pos) => {
			if (!gpsMarker && pos.info && pos.info.lng && pos.info.lat) {
				// Create GPS marker
				const el = gpsPulsingDot();
				gpsMarker = new maplibregl.Marker({
					element: el,
					offset: [-25, -25], // Offset by half the 50px width/height
				})
					.setLngLat([pos.info.lng, pos.info.lat])
					.addTo(map);
			} else if (gpsMarker && pos.info && pos.info.lng && pos.info.lat) {
				// Update GPS marker position
				gpsMarker.setLngLat([pos.info.lng, pos.info.lat]);
			}

			// Pan to GPS location if it exists
			if (pos.info && pos.info.lng && pos.info.lat) {
				map.panTo([pos.info.lng, pos.info.lat]);
			}
		});

		zzzz.on("gps.off", () => {
			if (gpsMarker) {
				gpsMarker.remove();
				gpsMarker = null;
			}
		});

		// Check if GPS position already exists on mount
		if (yyyy.pos && yyyy.pos.info && yyyy.pos.info.lng && yyyy.pos.info.lat) {
			const el = gpsPulsingDot();
			gpsMarker = new maplibregl.Marker({
				element: el,
				offset: [-25, -25], // Offset by half the 50px width/height
			})
				.setLngLat([yyyy.pos.info.lng, yyyy.pos.info.lat])
				.addTo(map);
		}

		// Initialize outage markers system
		initOutageMarkers(map);

		zzzz.on("redraw", async () => {
			console.log("redrawing...");
			console.log(yyyy.sesh.activeLivewire.wireident);
			let __ = map.getSource(yyyy.sesh.activeLivewire.wireident);
			console.log(__);
			__.setTiles(__.tiles);
			//map.redraw();
			//map.setStyle("");
			//map.setStyle(await getmapjson(), { diff: true });
			//map.triggerRepaint();
			//map.redraw();
		});

		zzzz.on("slides-enter", () => {
			if (!yyyy.sesh.activeLivewire) {
				map.setStyle(slidesMapStyle, { diff: false });
			}
		});

		zzzz.on("slides-leave", () => {
			if (!yyyy.sesh.activeLivewire) {
				map.setStyle(positron, { diff: false });
			}
		});

		/* the end */
	});

	onDestroy(() => {
		if (map) {
			// Clear any outage markers
			clearOutageMarkers();
			
			// Remove the map and free WebGL context
			map.remove();
			map = null;
		}
	});
</script>

<div
	id="maplibre-map"
	class:blurmap={(!yyyy.sesh.activeLivewire && !yyyy.slidesActive) || captchaActive}
	class:no-blurmap={yyyy.sesh.activeLivewire || yyyy.slidesActive || captchaActive}
></div>

<style>
	#map-stuff {
		position: absolute;
		z-index: 999;
		bottom: 4px;
		background-color: white;
	}

	#maplibre-map {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}

	.blurmap {
		filter: blur(1px) grayscale(0.8);
		transition: filter 5.5s ease-in-out;
	}

	.no-blurmap {
		filter: none;
		transition: filter 3.5s ease-in-out;
	}

	:global(.gps-pulsing-dot) {
		position: relative; /* Needed for absolute positioning of children */
		width: 50px; /* Adjust size as needed */
		height: 50px;
		display: block; /* Ensure block display */
	}

	:global(.gps-dot) {
		display: block;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%); /* Center the dot */
		width: 10px;
		height: 10px;
		border-radius: 50%;
		background-color: #ff3e00; /* Choose your color */
		z-index: 2; /* Ensure dot is above the circle */
		animation: pulse 1.5s infinite; /* Adjust duration as needed */
	}

	:global(.gps-circle) {
		display: block;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 30px; /* Initial size of the circle */
		height: 30px;
		border-radius: 50%;
		border: 2px solid #ff3e00; /* Match dot color or use a different one */
		z-index: 1;
		animation: shrink 2.5s infinite; /* Match dot animation duration */
	}

	@keyframes pulse {
		0% {
			transform: translate(-50%, -50%) scale(1); /* Start at normal size */
			opacity: 1;
		}
		50% {
			transform: translate(-50%, -50%) scale(1.2); /* Grow slightly */
			opacity: 0.7; /* Fade slightly */
		}
		100% {
			transform: translate(-50%, -50%) scale(1); /* Return to normal size */
			opacity: 1;
		}
	}

	@keyframes shrink {
		0% {
			transform: translate(-50%, -50%) scale(1); /* Start at normal size */
			opacity: 0.5; /* Start with some transparency */
		}
		100% {
			transform: translate(-50%, -50%) scale(0); /* Shrink to nothing */
			opacity: 0; /* Fade out */
		}
	}

	:global(.outage-icon) {
		width: 30px;
		height: 30px;
		background-color: #ff3e00;
		border: 2px solid white;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
		transition: all 0.2s ease;
	}

	:global(.outage-icon.highlighted) {
		width: 40px;
		height: 40px;
		background-color: #e63600;
		transform: scale(1.1);
		box-shadow: 0 4px 12px rgba(255, 62, 0, 0.6);
		z-index: 1000;
	}

	:global(.outage-number) {
		color: white;
		font-weight: bold;
		font-size: 12px;
		font-family: "Ubuntu", sans-serif;
		text-align: center;
		line-height: 1;
	}

	:global(.outage-icon.highlighted .outage-number) {
		font-size: 14px;
	}

	@keyframes dash {
		from {
			width: 35px;
			height: 35px;
		}
		to {
			width: 20px;
			height: 20px;
		}
	}

	:global(.pulse-container) {
		position: absolute;
		width: 60px;
		height: 60px;
		pointer-events: none;
		transform: translate(-50%, -50%);
	}

	:global(.pulse-circle) {
		position: absolute;
		width: 100%;
		height: 100%;
		border: 2px solid #0066ff;
		border-radius: 50%;
		opacity: 0.6;
		animation: pulse 2s infinite;
	}

	:global(.pulse-circle:nth-child(2)) {
		animation-delay: 0.5s;
	}

	:gloabal(.pulse-circle:nth-child(3)) {
		animation-delay: 1s;
	}

	@keyframes pulse {
		0% {
			transform: scale(0.8);
			opacity: 0.6;
		}
		50% {
			transform: scale(1.2);
			opacity: 0.3;
		}
		100% {
			transform: scale(0.8);
			opacity: 0.6;
		}
	}
</style>
